// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import { caption, headline } from '../typography';
import type { PickedTourImage, TourImageUploadPhase, SidebarSelection, GameDefinition, TourPlayerSnapshot, PlayerRow, Course, RecentCourse, TourRoundSummary, RoundGroupsData } from '../types';
import { sectionTitle } from '../utils';
import TourNameEditor from './editors/TourNameEditor';
import TourPlayersEditor from './editors/TourPlayersEditor';
import TourGamesEditor from './editors/TourGamesEditor';
import RoundSettingsEditor from './editors/RoundSettingsEditor';
import RoundPlayersEditor from './editors/RoundPlayersEditor';
import RoundGamesEditor from './editors/RoundGamesEditor';

type Props = {
  selection: SidebarSelection;
  allSteps: SidebarSelection[];
  tourId: string;
  tourName: string;
  onTourNameChange: (value: string) => void;
  onSendTourUpdate: (reason: string) => void;
  saving: boolean;
  error: string | null;
  statusNote: string;
  tourImageKey: string | null;
  tourImagePreviewUri: string | null;
  imageUploadPhase: TourImageUploadPhase;
  onUploadTourImage: (picked: PickedTourImage) => void;

  // Players
  playerRows: PlayerRow[];
  availablePlayers: TourPlayerSnapshot[];
  playerRowOpen: string | null;
  onSetPlayerRows: (updater: (prev: PlayerRow[]) => PlayerRow[]) => void;
  onSetPlayerRowOpen: (rowId: string | null) => void;
  onSendTourMemberAdd: (playerId: string, reason: string, draftRowId?: string) => void;
  onSendTourMemberRemove: (playerId: string) => void;
  onSendTemporaryPlayerCreate: (name: string) => string | undefined;

  // Games
  enabledGameDefinitions: GameDefinition[];
  tournamentGameIds: string[];
  tournamentWeightsByGameId: Record<string, number>;
  tournamentRuleSetByGameId: Record<string, string>;
  onUpdateTournamentGames: (gameIds: string[]) => void;
  onUpdateTournamentGameWeight: (gameId: string, weightPercent: number) => void;
  onUpdateTournamentGameRuleSet: (gameId: string, ruleSetId: string) => void;

  // Rounds data
  rounds: TourRoundSummary[];

  // Round course
  courses: Course[];
  recentCourses: RecentCourse[];
  loadingCourses: boolean;
  courseError: string | null;
  onAddCourse: (roundId: string) => void;

  // Round Players
  tourMemberOptions: Array<{ id: string; name: string }>;
  roundPlayerIdsByRoundId: Record<string, string[]>;
  playerIdsWithScoresByRoundId: Record<string, Set<string>>;
  playerNameById: Record<string, string>;
  onSendRoundMemberAdd: (roundId: string, playerId: string) => void;
  onSendRoundMemberRemove: (roundId: string, playerId: string) => void;
  onSendRoundGroupsSwap: (roundId: string, playerIdA: string, playerIdB: string) => void;
  roundGroupsByRoundId: Record<string, RoundGroupsData>;

  // Round Settings
  onUpdateRoundSettings: (roundId: string, updates: { name?: string; playDate?: string; courseName?: string; course?: Course; holesCount?: number }) => void;

  // Round Games
  roundGamesByRoundId: Record<
    string,
    { gameIds: string[]; weightsByGameId?: Record<string, number>; ruleSetByGameId?: Record<string, string> }
  >;
  onUpdateRoundGames: (roundId: string, gameIds: string[]) => void;
  onUpdateRoundGameWeight: (roundId: string, gameId: string, weightPercent: number) => void;
  onUpdateRoundGameRuleSet: (roundId: string, gameId: string, ruleSetId: string) => void;

  // Navigation
  isLastStep: boolean;
  onNext: () => void;
  onGoToTours: () => void;
  onAddRound: () => void;
  onSelectStep: (step: SidebarSelection) => void;

  // On mobile the section title is shown in the detail header above the panel.
  isMobile?: boolean;
};

export default function EditorPanel(props: Props) {
  const { selection, allSteps, error, statusNote, onGoToTours, onAddRound, onSelectStep } = props;

  const currentStepIndex = useMemo(() => {
    return allSteps.findIndex((step) => {
      if (step.kind !== selection.kind) return false;
      if ('roundId' in step && 'roundId' in selection) return step.roundId === selection.roundId;
      return !('roundId' in step) && !('roundId' in selection);
    });
  }, [allSteps, selection]);

  const isLast = currentStepIndex >= allSteps.length - 1;
  const isLastRoundSettings = selection.kind === 'round-settings' && currentStepIndex === allSteps.length - 3;

  const renderEditor = () => {
    switch (selection.kind) {
      case 'tour-name':
        return (
          <TourNameEditor
            tourName={props.tourName}
            onNameChange={props.onTourNameChange}
            onSave={props.onSendTourUpdate}
            saving={props.saving}
            tourId={props.tourId}
            imageKey={props.tourImageKey}
            imagePreviewUri={props.tourImagePreviewUri}
            uploadPhase={props.imageUploadPhase}
            onUploadImage={props.onUploadTourImage}
          />
        );

      case 'tour-players':
        return (
          <TourPlayersEditor
            playerRows={props.playerRows}
            availablePlayers={props.availablePlayers}
            playerRowOpen={props.playerRowOpen}
            saving={props.saving}
            onSetPlayerRows={props.onSetPlayerRows}
            onSetPlayerRowOpen={props.onSetPlayerRowOpen}
            onSendTourMemberAdd={props.onSendTourMemberAdd}
            onSendTourMemberRemove={props.onSendTourMemberRemove}
            onSendTemporaryPlayerCreate={props.onSendTemporaryPlayerCreate}
          />
        );

      case 'tour-games':
        return (
          <TourGamesEditor
            enabledGameDefinitions={props.enabledGameDefinitions}
            tournamentGameIds={props.tournamentGameIds}
            weightsByGameId={props.tournamentWeightsByGameId}
            ruleSetByGameId={props.tournamentRuleSetByGameId}
            playerCount={props.playerRows.length}
            roundCount={props.rounds.length}
            onToggleGame={props.onUpdateTournamentGames}
            onWeightChange={props.onUpdateTournamentGameWeight}
            onRuleSetChange={props.onUpdateTournamentGameRuleSet}
          />
        );

      case 'round-settings': {
        const round = props.rounds.find((r) => r.id === selection.roundId);
        if (!round) return null;
        return (
          <RoundSettingsEditor
            round={round}
            courses={props.courses}
            recentCourses={props.recentCourses}
            loadingCourses={props.loadingCourses}
            courseError={props.courseError}
            onAddCourse={() => props.onAddCourse(round.id)}
            onUpdateRoundSettings={props.onUpdateRoundSettings}
          />
        );
      }

      case 'round-players': {
        return (
          <RoundPlayersEditor
            roundId={selection.roundId}
            playerIdsWithScores={props.playerIdsWithScoresByRoundId[selection.roundId]}
            tourMembers={props.tourMemberOptions}
            roundPlayerIds={props.roundPlayerIdsByRoundId[selection.roundId] ?? []}
            roundGroups={props.roundGroupsByRoundId[selection.roundId]}
            playerNameById={props.playerNameById}
            onAddRoundMember={props.onSendRoundMemberAdd}
            onRemoveRoundMember={props.onSendRoundMemberRemove}
            onSwapPlayers={props.onSendRoundGroupsSwap}
          />
        );
      }

      case 'round-games':
        return (
          <RoundGamesEditor
            roundId={selection.roundId}
            enabledGameDefinitions={props.enabledGameDefinitions}
            roundGameIds={props.roundGamesByRoundId[selection.roundId]?.gameIds ?? []}
            weightsByGameId={props.roundGamesByRoundId[selection.roundId]?.weightsByGameId ?? {}}
            ruleSetByGameId={props.roundGamesByRoundId[selection.roundId]?.ruleSetByGameId ?? {}}
            tournamentGameIds={props.tournamentGameIds}
            tournamentWeightsByGameId={props.tournamentWeightsByGameId}
            tournamentRuleSetByGameId={props.tournamentRuleSetByGameId}
            playerCount={(props.roundPlayerIdsByRoundId[selection.roundId] ?? []).length}
            onUpdateRoundGames={props.onUpdateRoundGames}
            onWeightChange={props.onUpdateRoundGameWeight}
            onRuleSetChange={props.onUpdateRoundGameRuleSet}
          />
        );

      default:
        return null;
    }
  };

  const renderNav = () => {
    if (isLastRoundSettings) {
      const nextStep = allSteps[currentStepIndex + 1];
      return (
        <View style={styles.navChoices}>
          <Text style={styles.navPrompt}>What would you like to do next?</Text>
          <View style={styles.navButtons}>
            <Pressable style={styles.secondaryButton} onPress={onGoToTours}>
              <Text style={styles.secondaryButtonText}>Back to tours</Text>
            </Pressable>
            <Pressable style={styles.secondaryButton} onPress={onAddRound}>
              <Text style={styles.secondaryButtonText}>Add another round</Text>
            </Pressable>
            <Pressable
              style={styles.primaryButton}
              onPress={() => { if (nextStep) onSelectStep(nextStep); }}
            >
              <Text style={styles.primaryButtonText}>Configure round</Text>
            </Pressable>
          </View>
        </View>
      );
    }

    if (isLast) {
      // A tour with no rounds is not set up yet, so do not congratulate the user
      // on finishing — a brand-new tour reaches this step after only three.
      const hasRounds = props.rounds.length > 0;
      return (
        <View style={styles.navChoices}>
          <Text style={styles.navPrompt}>
            {hasRounds
              ? 'All done! What would you like to do next?'
              : 'Your tour needs at least one round to play.'}
          </Text>
          <View style={styles.navButtons}>
            <Pressable style={styles.secondaryButton} onPress={onGoToTours}>
              <Text style={styles.secondaryButtonText}>Back to tours</Text>
            </Pressable>
            <Pressable style={styles.primaryButton} onPress={onAddRound}>
              <Text style={styles.primaryButtonText}>Add a round</Text>
            </Pressable>
          </View>
        </View>
      );
    }

    const nextStep = allSteps[currentStepIndex + 1];
    return (
      <Pressable
        style={styles.nextButton}
        onPress={() => { if (nextStep) onSelectStep(nextStep); }}
      >
        <Text style={styles.nextButtonText}>Next</Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      {(error || statusNote) ? (
        <View style={styles.statusBar}>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {statusNote && !error ? <Text style={styles.statusText}>{statusNote}</Text> : null}
        </View>
      ) : null}
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {!props.isMobile ? (
          <Text style={styles.sectionTitle}>{sectionTitle(selection, props.rounds)}</Text>
        ) : null}
        {renderEditor()}
        <View style={styles.navSection}>
          {renderNav()}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  statusBar: {
    paddingHorizontal: 20,
    paddingVertical: 6,
    backgroundColor: appColors.surface.base,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  errorText: {
    fontSize: 12,
    color: appColors.status.danger,
    fontWeight: '600',
  },
  statusText: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 40,
    maxWidth: 600,
  },
  sectionTitle: {
    ...headline,
    marginBottom: 18,
  },
  navSection: {
    marginTop: 28,
    paddingTop: 20,
  },
  nextButton: {
    alignSelf: 'flex-end',
    paddingHorizontal: 28,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: appColors.button.primaryBg,
    borderWidth: 1,
    borderColor: appColors.button.primaryBorder,
  },
  nextButtonText: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 2.1,
    textTransform: 'uppercase',
    color: appColors.button.primaryText,
  },
  navChoices: {
    gap: 14,
  },
  navPrompt: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  navButtons: {
    flexDirection: 'row',
    // Uppercase tracked labels are wide; without wrapping the third choice runs
    // off the right edge on a phone.
    flexWrap: 'wrap',
    gap: 12,
  },
  primaryButton: {
    paddingHorizontal: 24,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: appColors.button.primaryBg,
    borderWidth: 1,
    borderColor: appColors.button.primaryBorder,
  },
  primaryButtonText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 2.1,
    textTransform: 'uppercase',
    color: appColors.button.primaryText,
  },
  secondaryButton: {
    paddingHorizontal: 24,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: appColors.button.secondaryBg,
    borderWidth: 1,
    borderColor: appColors.button.secondaryBorder,
  },
  secondaryButtonText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 2.1,
    textTransform: 'uppercase',
    color: appColors.button.secondaryText,
  },
});
